import { useEffect, useState } from "react";

export type MotionTier = "reduced" | "low" | "medium" | "high";

type NetworkInformation = {
  saveData?: boolean;
  addEventListener?: (type: "change", listener: () => void) => void;
  removeEventListener?: (type: "change", listener: () => void) => void;
};

type NavigatorWithCapabilities = Navigator & {
  connection?: NetworkInformation;
  deviceMemory?: number;
};

function selectMotionTier(
  prefersReducedMotion: boolean,
  saveData: boolean,
  deviceMemory: number,
): MotionTier {
  if (prefersReducedMotion) return "reduced";
  if (saveData || deviceMemory <= 4) return "low";
  if (deviceMemory <= 8) return "medium";
  return "high";
}

function readMotionTier(): MotionTier {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "low";
  }

  const capableNavigator = navigator as NavigatorWithCapabilities;
  const prefersReducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return selectMotionTier(
    prefersReducedMotion,
    Boolean(capableNavigator.connection?.saveData),
    capableNavigator.deviceMemory ?? 8,
  );
}

export function useMotionPolicy(): MotionTier {
  // Resolve the first frame synchronously so capable desktops never flash the
  // reduced-motion end poster before mounting the opening film frame.
  const [tier, setTier] = useState<MotionTier>(readMotionTier);

  useEffect(() => {
    const reducedMotionQuery = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    const capableNavigator = navigator as NavigatorWithCapabilities;
    const connection = capableNavigator.connection;

    const updateTier = () => {
      setTier(readMotionTier());
    };

    let removeReducedMotionListener: () => void;
    if (typeof reducedMotionQuery.addEventListener === "function") {
      reducedMotionQuery.addEventListener("change", updateTier);
      removeReducedMotionListener = () => {
        reducedMotionQuery.removeEventListener("change", updateTier);
      };
    } else {
      reducedMotionQuery.addListener(updateTier);
      removeReducedMotionListener = () => {
        reducedMotionQuery.removeListener(updateTier);
      };
    }
    connection?.addEventListener?.("change", updateTier);

    return () => {
      removeReducedMotionListener();
      connection?.removeEventListener?.("change", updateTier);
    };
  }, []);

  return tier;
}
